import React, { useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import CustomerProfileService from './CustomerProfileService';

const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    maxHeight: '90vh',
    width: '640px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    overflow: 'auto',
  },
};

const Field = ({ label, value }) => (
  <div className="text-sm">
    <span className="text-gray-500">{label}: </span>
    <span className="font-medium">{value || '-'}</span>
  </div>
);

// `profile` is the OLD chat profile (A, userId=null). Staff search for the
// customer's self-registered account (B) and merge it into A.
const MergeAccountModal = ({ isOpen, onClose, profile }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [foundB, setFoundB] = useState(null);
  const [merging, setMerging] = useState(false);

  const handleClose = () => {
    setQuery('');
    setFoundB(null);
    onClose();
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setFoundB(null);
    try {
      const isEmail = q.includes('@');
      const res = isEmail
        ? await CustomerProfileService.findProfileByContact(q, profile.shop)
        : await CustomerProfileService.findProfileByPhone(q.replace(/\D/g, ''), profile.shop);
      setFoundB(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'ไม่พบบัญชี');
    } finally {
      setSearching(false);
    }
  };

  const sameProfile = foundB && foundB.id === profile.id;
  const notPlatform = foundB && !foundB.userId;
  const canMerge = foundB && !sameProfile && !notPlatform;

  const handleMerge = async () => {
    if (!canMerge) return;
    setMerging(true);
    try {
      await CustomerProfileService.mergeAccounts(profile.id, foundB.id);
      toast.success('รวมบัญชีสำเร็จ');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err.response?.data?.message || 'รวมบัญชีไม่สำเร็จ');
      setMerging(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} style={modalStyles} ariaHideApp={false}>
      <div className="p-6">
        <h2 className="mb-1 text-2xl font-bold">รวมกับบัญชีที่ลูกค้าสมัครเอง</h2>
        <p className="mb-4 text-sm text-gray-500">
          ค้นหาบัญชีแพลตฟอร์มที่ลูกค้าสมัครเอง แล้วรวมประวัติเข้ากับโปรไฟล์เดิมนี้
        </p>

        {/* Old profile (A) */}
        <div className="mb-4 rounded-lg border border-gray-200 p-3">
          <div className="mb-1 text-xs font-semibold text-indigo-700">โปรไฟล์เดิม (เก็บไว้)</div>
          <Field label="ชื่อ" value={profile?.customerName} />
          <Field label="เบอร์" value={profile?.phone} />
          <Field label="Smile Name" value={profile?.smileName} />
        </div>

        {/* Search */}
        <div className="mb-4 flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="อีเมล หรือ เบอร์โทรของบัญชีใหม่"
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={handleSearch}
            disabled={searching}
            className="rounded-md bg-slate-700 px-4 py-2 text-white hover:bg-slate-800 disabled:bg-gray-400"
          >
            {searching ? 'กำลังค้นหา...' : 'ค้นหา'}
          </button>
        </div>

        {/* Found account (B) */}
        {foundB && (
          <div className="mb-4 rounded-lg border border-gray-200 p-3">
            <div className="mb-1 text-xs font-semibold text-purple-700">บัญชีใหม่ (จะถูกรวมและปิดใช้งาน)</div>
            <Field label="ชื่อ" value={foundB.customerName} />
            <Field label="เบอร์" value={foundB.phone} />
            <Field label="Smile Name" value={foundB.smileName} />
            <Field label="ติดต่อ" value={foundB.contact} />
            {sameProfile && (
              <div className="mt-2 text-sm font-semibold text-red-600">นี่คือโปรไฟล์เดียวกัน — เลือกบัญชีอื่น</div>
            )}
            {notPlatform && (
              <div className="mt-2 text-sm font-semibold text-red-600">
                บัญชีนี้ยังไม่ได้สมัครแพลตฟอร์ม (ไม่มี User) — ใช้ &quot;เชื่อมเข้าแพลตฟอร์ม&quot; แทน
              </div>
            )}
          </div>
        )}

        {canMerge && (
          <div className="mb-4 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800">
            จะย้ายประวัติทั้งหมด (ออเดอร์, ที่อยู่, การจัดส่ง ฯลฯ) ของบัญชีใหม่มารวมกับโปรไฟล์เดิม
            เก็บเรทและสิทธิ์ของโปรไฟล์เดิมไว้ ส่วนกระเป๋าเงิน/พอยต์ของลูกค้ายังอยู่กับล็อกอินเดิม
            แล้วปิดใช้งานบัญชีใหม่ (เก็บเป็นประวัติ)
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleMerge}
            disabled={!canMerge || merging}
            className="flex-1 rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:bg-gray-400"
          >
            {merging ? 'กำลังรวม...' : 'ยืนยันรวมบัญชี'}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md bg-gray-500 px-4 py-2 text-white hover:bg-gray-600"
          >
            ปิด
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MergeAccountModal;
